import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import GeneralDialog from '../../../shared/general-dialog';
import { GridColDef } from '@mui/x-data-grid-pro';
import { UserKeys, getContactSyncUserModels } from '../../../api/users-api';
import { ContactSyncUserModels, Page } from '../../../../types';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import PersonOff from '@mui/icons-material/PersonOff';

interface ContactSyncUserStatusProps {
  open: boolean;
  handleClose: () => void;
  contactSyncId: number;
}

export default function ContactSyncUserStatusDialog({
  open,
  handleClose,
  contactSyncId,
}: ContactSyncUserStatusProps) {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
  }>({
    pageSize: 100,
    page: 0,
    cursor: null,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.ContactSyncUserModels,
      contactSyncId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () => {
      const object: Page<ContactSyncUserModels[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && data && data.queries) {
        object.queries = {
          [Object.keys(data.queries)[0]]: paginationModel.cursor || '',
        };
      }
      return getContactSyncUserModels(object, contactSyncId);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading the user contact sync models. Please refresh and try again.',
    },
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'userEmail',
        headerName: 'Email',
        editable: false,
        sortable: false,
        flex: 1,
      },
      {
        field: 'lastError',
        headerName: 'Last Error',
        editable: false,
        sortable: false,
        flex: 1,
      },
      {
        field: 'lastCompleteTime',
        headerName: 'Last Completion Time',
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const date = new Date(params.value);
          return date.toLocaleString();
        },
      },
      {
        field: 'destGroupCreationTime',
        headerName: 'Group Creation Time',
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          if (params.value === 0) {
            return '';
          } else {
            const date = new Date(params.value);
            return date.toLocaleString();
          }
        },
      },
    ],
    []
  );

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title="Destination User Status"
      maxWidth="md"
      dialogContent={
        <Stack gap={2}>
          <Box sx={{ height: 500, pl: 2, pr: 2 }}>
            <SharedDataGridServer
              showToolbar={false}
              slots={{
                ...createGridOverlays(
                  'Users',
                  <PersonOff />,
                  'Try changing your search term.'
                ),
              }}
              pageSizeOptions={[100]}
              getRowId={(row) => row.userEmail}
              disableRowSelectionOnClick
              disableColumnMenu
              columns={columns}
              data={data}
              loading={isLoading || isFetching}
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => {
                if (isFetching || isLoading) return;

                if (newModel.pageSize !== paginationModel.pageSize) {
                  setPaginationModel({
                    page: 0,
                    pageSize: newModel.pageSize,
                    cursor: null,
                  });
                } else {
                  setPaginationModel({
                    ...newModel,
                    cursor: data?.cursor || '',
                  });
                }
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'default',
                },
              }}
            />
          </Box>
        </Stack>
      }
      primaryButton={{
        loading: false,
        action: handleClose,
        disabled: false,
        title: 'Close',
      }}
    />
  );
}
